import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';
const money = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One pending review request — its lines + a per-line notes box that
 * autosaves ~800ms after the user stops typing, plus a "Send Reply" button
 * that emails the answer back to whoever originally sent the request. */
function RequestCard({ request, onAnswered }) {
  const [notes, setNotes] = useState(() => {
    const initial = {};
    Object.entries(request.notes || {}).forEach(([k, v]) => { initial[k] = v.text || ''; });
    return initial;
  });
  const [saving, setSaving] = useState({});
  const [sending, setSending] = useState(false);
  const timers = useRef({});

  const saveNote = useCallback(async (orderNumber, text) => {
    setSaving(s => ({ ...s, [orderNumber]: true }));
    try {
      await api.patch(`${API}/order-book-reports/review-requests/${request.id}/notes`, {
        order_number: orderNumber, text,
      });
    } catch {
      toast.error('Failed to autosave note');
    } finally {
      setSaving(s => ({ ...s, [orderNumber]: false }));
    }
  }, [request.id]);

  const handleNoteChange = (orderNumber, text) => {
    setNotes(prev => ({ ...prev, [orderNumber]: text }));
    if (timers.current[orderNumber]) clearTimeout(timers.current[orderNumber]);
    timers.current[orderNumber] = setTimeout(() => saveNote(orderNumber, text), 800);
  };

  const handleSendReply = async () => {
    setSending(true);
    try {
      await api.post(`${API}/order-book-reports/review-requests/${request.id}/answer`);
      toast.success(`Reply sent to ${request.sender_name}`);
      onAnswered(request.id);
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`review-request-card-${request.id}`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: enconOrange }}>
          Request from {request.sender_name}
        </p>
        <p className="text-sm text-gray-600 mt-0.5">{request.message}</p>
      </div>

      <div className="space-y-2">
        {(request.lines || []).map(line => (
          <div key={line.order_number} className="bg-gray-50 rounded-lg p-3" data-testid={`review-answer-line-${line.order_number}`}>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-semibold text-gray-800">{line.customer_name} <span className="text-gray-400 font-mono">#{line.order_number}</span></span>
              <span className="text-gray-500">{money(line.contribution_value)} ({(line.contribution_pct || 0).toFixed(2)}%)</span>
            </div>
            <textarea
              value={notes[line.order_number] || ''}
              onChange={(e) => handleNoteChange(line.order_number, e.target.value)}
              placeholder="Add your notes here…"
              rows={2}
              data-testid={`review-answer-notes-${line.order_number}`}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 resize-none bg-white"
            />
            <div className="text-[11px] text-gray-400 mt-0.5 h-3">
              {saving[line.order_number] ? 'Saving…' : (notes[line.order_number] ? <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span> : '')}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSendReply}
          disabled={sending}
          className="text-white"
          style={{ backgroundColor: enconOrange }}
          data-testid={`review-answer-send-btn-${request.id}`}
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
          Send Reply to {request.sender_name}
        </Button>
      </div>
    </div>
  );
}

/** "Send Answers" modal — every pending review request addressed to the
 * current user, grouped by sender. Cannot be dismissed by clicking outside;
 * only the explicit Close button works. Notes autosave as you type. */
export default function OrderBookAnswerModal({ isOpen, onClose }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/order-book-reports/review-requests/pending`);
      setRequests(res.data?.requests || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const handleAnswered = (id) => setRequests(prev => prev.filter(r => r.id !== id));

  return (
    <Dialog open={isOpen} onOpenChange={() => { /* only the explicit Close button can dismiss this modal */ }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="review-answers-modal"
      >
        <DialogHeader className="flex-row items-center justify-between">
          <DialogTitle>Send Answers — Order Book Review</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-14"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
          ) : requests.length === 0 ? (
            <div className="text-center py-14 text-gray-400 text-sm">No pending review requests — you're all caught up.</div>
          ) : (
            requests.map(req => <RequestCard key={req.id} request={req} onAnswered={handleAnswered} />)
          )}

          <div className="flex justify-end pt-1">
            <Button variant="outline" onClick={onClose} data-testid="review-answers-close-btn">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
